// src/aws/requestQueueManager.js
import pLimit from 'p-limit';

class RequestQueueManager {
  constructor(options = {}) {
    this.maxDelay = options.maxDelay || 2000; // Max delay for backoff

    // Service-specific rate limiting via a token bucket per AWS service.
    // Each bucket refills at `refillRatePerSec` tokens/sec up to `capacity` (burst).
    // Token acquisition is serialized per-service (see acquireToken) so that two different
    // resource types can never both observe an available token and fire back-to-back —
    // the race the old per-resourceType mutex + shared lastCallTime allowed.
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.serviceConfig = {
      connect: {
        refillRatePerSec: 2,   // 2 calls/second steady-state (proven stable for Connect)
        capacity: 5,           // burst headroom for parallel Search pages
        concurrency: 3,        // concurrent in-flight Connect requests within track
        delay: 500,            // retained for monitoring display (1000/refillRatePerSec)
        tokens: 5,
        lastRefill: now,
        acquireLock: Promise.resolve(),
        requestCount: 0
      },
      lambda: {
        refillRatePerSec: 3.3, // ~3.3 calls/second (aggressive but safe)
        capacity: 4,
        concurrency: 1,
        delay: 300,
        tokens: 4,
        lastRefill: now,
        acquireLock: Promise.resolve(),
        requestCount: 0
      },
      lex: {
        refillRatePerSec: 5,   // Lex model-building is a separate service quota; the fetch
        capacity: 8,           // fans out (bots/intents/slots in parallel), so allow real
        concurrency: 4,        // concurrency. Circuit breaker backs off if Lex throttles.
        delay: 200,            // retained for monitoring display (1000/refillRatePerSec)
        tokens: 8,
        lastRefill: now,
        acquireLock: Promise.resolve(),
        requestCount: 0
      },
      lexAnalytics: {
        refillRatePerSec: 1,   // The Lex V2 analytics sub-API (ListSessionMetrics /
        capacity: 2,           // ListUtteranceMetrics) has a much lower throttle quota than
        concurrency: 1,        // the model-building List/Describe APIs, so it gets its own
        delay: 1000,           // slow, serialized bucket. retained for monitoring display.
        tokens: 2,
        lastRefill: now,
        acquireLock: Promise.resolve(),
        requestCount: 0
      },
      trustedAdvisor: {
        refillRatePerSec: 3.3, // ~3.3 calls/second
        capacity: 4,
        concurrency: 1,
        delay: 300,
        tokens: 4,
        lastRefill: now,
        acquireLock: Promise.resolve(),
        requestCount: 0
      }
    };

    // Retained for getMonitoringStats() compatibility. Per-resource pacing is now handled
    // entirely by the per-service token buckets; no resource needs a tighter-than-service
    // cap since the slowest path (DescribeUser) was replaced by SearchUsers.
    this.resourceSpecificDelays = {
      ...options.resourceSpecificDelays
    };

    // Resource-specific limiters for fine-grained concurrency control (optional overrides)
    this.resourceSpecificLimiters = {
      ...options.resourceSpecificLimiters
    };

    // Service-specific limiters for concurrent processing
    this.serviceLimiters = {
      connect: pLimit(this.serviceConfig.connect.concurrency),
      lambda: pLimit(this.serviceConfig.lambda.concurrency),
      lex: pLimit(this.serviceConfig.lex.concurrency),
      lexAnalytics: pLimit(this.serviceConfig.lexAnalytics.concurrency),
      trustedAdvisor: pLimit(this.serviceConfig.trustedAdvisor.concurrency)
    };

    this.rateLimitViolationCount = 0; // Track potential violations

    // Resource-type-specific circuit breaker state (still useful for error handling)
    this.consecutiveErrorsByType = new Map(); // resourceType -> consecutive error count
    this.circuitBreakersByType = new Map(); // resourceType -> boolean (open/closed)
    this.circuitBreakerResetTimesByType = new Map(); // resourceType -> timestamp

  }

  // Get circuit breaker threshold for a specific resource type
  getCircuitBreakerThreshold(resourceType) {
    // Reduced threshold for faster circuit breaker activation
    // Since we disabled AWS SDK retries, we can be more aggressive
    return 3; // Was 5, now 3 for faster protection
  }

  // Get resource-type-specific consecutive errors count
  getConsecutiveErrors(resourceType) {
    return this.consecutiveErrorsByType.get(resourceType) || 0;
  }

  // Set resource-type-specific consecutive errors count
  setConsecutiveErrors(resourceType, count) {
    this.consecutiveErrorsByType.set(resourceType, count);
  }

  // Check if circuit breaker is open for a specific resource type
  isCircuitBreakerOpen(resourceType) {
    return this.circuitBreakersByType.get(resourceType) || false;
  }

  // Get circuit breaker reset time for a specific resource type
  getCircuitBreakerResetTime(resourceType) {
    return this.circuitBreakerResetTimesByType.get(resourceType) || 0;
  }

  // Open circuit breaker for a specific resource type with jitter
  openCircuitBreaker(resourceType, delayDuration) {
    // CRITICAL FIX: Apply jitter to delay DURATION, not absolute timestamp
    // Add jitter (±25%) to prevent thundering herd when multiple circuit breakers reset
    const jitterMs = delayDuration * 0.25 * (Math.random() - 0.5); // ±25% jitter on duration
    const jitteredDelay = delayDuration + jitterMs;
    const jitteredResetTime = Date.now() + jitteredDelay;

    this.circuitBreakersByType.set(resourceType, true);
    this.circuitBreakerResetTimesByType.set(resourceType, jitteredResetTime);

    // Log circuit breaker activation for monitoring
    const waitSeconds = Math.round(jitteredDelay / 1000);
    const jitterSeconds = Math.round(jitterMs / 1000);
    console.warn(`🚫 Circuit breaker OPENED for ${resourceType}, will reset in ${waitSeconds}s (jitter: ${jitterSeconds >= 0 ? '+' : ''}${jitterSeconds}s)`);
  }

  // Reset circuit breaker for a specific resource type
  resetCircuitBreaker(resourceType) {
    // Simplified circuit breaker reset - treat all resources equally
    // This allows proper retry mechanisms to work without interference
    this.circuitBreakersByType.set(resourceType, false);
    this.setConsecutiveErrors(resourceType, 0);
  }

  // Explicitly close circuit breaker after successful request (for half-open state)
  closeCircuitBreaker(resourceType) {
    const wasOpen = this.isCircuitBreakerOpen(resourceType);
    this.circuitBreakersByType.set(resourceType, false);
    this.circuitBreakerResetTimesByType.delete(resourceType);
    this.setConsecutiveErrors(resourceType, 0);
    if (wasOpen) {
      console.log(`✅ Circuit breaker CLOSED for ${resourceType} - service has recovered`);
    }
  }

  // Reset circuit breakers between instances (preserving global rate limiting state)
  resetForNewInstance() {
    // DO NOT reset global rate limiting state - this was the critical bug!
    // Rate limiting must persist across ALL resource types and instances
    // Only reset circuit breaker states for error handling
    
    
    // Clear all circuit breaker states - treat all resources equally
    this.consecutiveErrorsByType.clear();
    this.circuitBreakersByType.clear();
    this.circuitBreakerResetTimesByType.clear();

    // CRITICAL: Do NOT touch the per-service token buckets here. Leaving tokens/lastRefill
    // intact keeps rate limiting continuous across all resource types and instances.
  }

  dispatchThrottleEvent(isThrottled, message) {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('throttle-status', {
        detail: { isThrottled, message }
        }));
    }
  }

  async executeRequest(fn, options = {}) {
    const {
      retryCount = 0,
      maxRetries = 3, // Lowered from 5: the per-service token bucket prevents most 429s
      resourceType = 'unknown'
    } = options;

    // Check resource-type-specific circuit breaker before starting
    if (this.isCircuitBreakerOpen(resourceType)) {
      const resetTime = this.getCircuitBreakerResetTime(resourceType);
      const now = Date.now();
      if (now < resetTime) {
        const waitTime = resetTime - now;
        const message = `Circuit breaker active for ${resourceType}. Waiting ${Math.round(waitTime/1000)} seconds...`;
        console.warn(`🚫 ${message}`);
        this.dispatchThrottleEvent(true, message);

        // CRITICAL FIX: Actually wait for circuit breaker to reset instead of throwing immediately
        // Add 100ms buffer to ensure we're past the reset time
        await this.delay(waitTime + 100);

        // After waiting, the circuit breaker should be in half-open state
        console.log(`🔄 Circuit breaker wait complete for ${resourceType}. Attempting request...`);
        this.dispatchThrottleEvent(false, '');
      } else {
        // Circuit breaker timeout has passed - enter half-open state
        console.log(`🔄 Circuit breaker for ${resourceType} entering half-open state (reset time: ${resetTime}, now: ${now})`);
        // Don't reset yet - let the actual request test if service has recovered
      }
    }

    // Retry loop - OUTSIDE the limiter to avoid deadlock
    let currentRetry = retryCount;
    
    while (currentRetry <= maxRetries) {
      try {
        // This call acquires and releases the limiter properly
        const result = await this.executeSingleRequest(fn, resourceType);
        
        return result;
        
      } catch (error) {
        // A resource-count SERVICE QUOTA (e.g. max queues per Connect instance) surfaces as
        // LimitExceededException — HTTP 429, but PERMANENT, not transient throttling. Retrying /
        // circuit-breaking it just burns the backoff windows and ends in a misleading "circuit breaker"
        // error, so it must NOT be classified as a rate-limit error: let it fall through and throw
        // immediately with the real AWS message.
        const isServiceQuotaError = error.name === 'LimitExceededException' ||
                                error.name === 'ServiceQuotaExceededException';

        // Handle throttling errors AND timeouts as rate limit violations
        const isRateLimitError = !isServiceQuotaError && (
                                error.name === 'TooManyRequestsException' ||
                                error.name === 'ThrottlingException' ||
                                error.$metadata?.httpStatusCode === 429);

        const isTimeoutError = error.name === 'TimeoutError' || 
                              error.message?.includes('timeout') ||
                              error.message?.includes('Request timeout');
        
        if (isRateLimitError || isTimeoutError) {
          const consecutiveErrors = this.getConsecutiveErrors(resourceType) + 1;
          this.setConsecutiveErrors(resourceType, consecutiveErrors);

          const errorType = isTimeoutError ? 'timeout' : 'rate limit';

          // Open circuit breaker if too many consecutive errors for this resource type
          const threshold = this.getCircuitBreakerThreshold(resourceType);
          if (consecutiveErrors >= threshold) {
            // Progressive backoff with jitter: 5s, 10s, 15s, 20s, 25s, 30s max
            const baseDelay = Math.min(30000, 5000 * Math.min(consecutiveErrors - (threshold - 1), 4));

            // CRITICAL FIX: Pass delay DURATION to openCircuitBreaker, not absolute timestamp
            this.openCircuitBreaker(resourceType, baseDelay);

            const finalDelay = this.getCircuitBreakerResetTime(resourceType) - Date.now();
            console.warn(`🚫 Circuit breaker opened for ${resourceType} for ~${Math.round(finalDelay/1000)} seconds after ${consecutiveErrors} consecutive ${errorType} errors`);
            this.dispatchThrottleEvent(true, `Circuit breaker activated for ${resourceType}. Pausing for ~${Math.round(finalDelay/1000)} seconds...`);

            // Circuit breaker is now open - fail immediately instead of retrying
            throw new Error(`Circuit breaker opened for ${resourceType} after ${consecutiveErrors} consecutive ${errorType} errors. Retry after ~${Math.round(finalDelay/1000)} seconds.`);
          }

          // Check if we should retry
          if (currentRetry < maxRetries) {
            const backoffDelay = this.calculateBackoffDelay(currentRetry);
            this.dispatchThrottleEvent(true, `AWS API ${errorType} for ${resourceType}. Retrying in ${backoffDelay}ms...`);
            
            await this.delay(backoffDelay);
            currentRetry++;
            continue; // Try again
          } else {
            console.warn(`❌ Max retries (${maxRetries}) reached for ${resourceType}. Request failed.`);
            this.dispatchThrottleEvent(false, '');
            throw error;
          }
        }
        
        // Non-rate-limit error - throw immediately
        throw error;
      }
    }
  }

  // Single API call without retry logic - to avoid limiter deadlock
  async executeSingleRequest(fn, resourceType) {
    // Acquire a token from this resource's service bucket BEFORE entering the concurrency
    // limiter. Token acquisition is serialized per-service, so spacing is enforced against
    // the same dimension (the AWS service) that the throttle quota applies to.
    const service = this.getServiceForResource(resourceType);
    await this.acquireToken(service);

    // Use resource-specific limiter for concurrency control AFTER rate limiting
    const limiter = this.getLimiterForResource(resourceType);

    return limiter(async () => {
      try {
        const result = await fn();

        // Success! Reset consecutive errors and close circuit breaker if it was open
        this.setConsecutiveErrors(resourceType, 0);
        if (this.isCircuitBreakerOpen(resourceType)) {
          // Use closeCircuitBreaker for explicit closure with logging
          this.closeCircuitBreaker(resourceType);
        }

        return result;
      } catch (error) {
        // Don't handle retries here - just throw the error up to executeRequest
        throw error;
      }
    });
  }

  // Get the appropriate service for a resource type
  getServiceForResource(resourceType) {
    // Map resource types to their corresponding AWS service
    if (['instanceConfig', 'agentStatuses', 'hoursOfOperation', 'contactFlows', 'contactFlowModules',
         'phoneNumbers', 'prompts', 'queues', 'quickConnects', 'routingProfiles',
         'securityProfiles', 'users', 'userHierarchyGroups'].includes(resourceType)) {
      return 'connect';
    }
    if (resourceType === 'functions') {
      return 'lambda';
    }
    if (resourceType === 'lexBotV2') {
      return 'lex';
    }
    if (resourceType === 'lexAnalytics') {
      return 'lexAnalytics';
    }
    if (resourceType === 'recommendations') {
      return 'trustedAdvisor';
    }
    // Default to connect for unknown resources
    return 'connect';
  }

  // Get the appropriate limiter for a resource type
  getLimiterForResource(resourceType) {
    // Check for resource-specific limiter first (for sensitive APIs like user-describe)
    if (this.resourceSpecificLimiters[resourceType]) {
      return this.resourceSpecificLimiters[resourceType];
    }

    // Fall back to service-level limiter
    const service = this.getServiceForResource(resourceType);
    return this.serviceLimiters[service];
  }

  // Refill a service bucket based on elapsed wall-clock time (capped at capacity).
  _refillBucket(bucket) {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const elapsedSec = (now - bucket.lastRefill) / 1000;
    if (elapsedSec > 0) {
      bucket.tokens = Math.min(bucket.capacity, bucket.tokens + elapsedSec * bucket.refillRatePerSec);
      bucket.lastRefill = now;
    }
  }

  // Acquire one token from a service's bucket. Acquisitions are chained per-service so they
  // run strictly one at a time: each caller refills, consumes a token if available, or waits
  // exactly long enough for the next token to accrue. This single per-service serialization
  // point is what prevents two resource types from racing past the same rate limit.
  async acquireToken(service) {
    const bucket = this.serviceConfig[service] || this.serviceConfig.connect;

    // Chain onto the previous acquisition so only one runs at a time for this service.
    const previous = bucket.acquireLock;
    let release;
    bucket.acquireLock = new Promise((resolve) => { release = resolve; });

    try {
      await previous;

      this._refillBucket(bucket);
      if (bucket.tokens < 1) {
        const deficit = 1 - bucket.tokens;
        const waitMs = Math.ceil((deficit / bucket.refillRatePerSec) * 1000);
        await this.delay(waitMs);
        this._refillBucket(bucket);
      }

      bucket.tokens -= 1;
      bucket.requestCount++;
    } finally {
      release();
    }
  }

  calculateBackoffDelay(retryCount) {
    // Enhanced exponential backoff with jitter for better recovery
    // 250ms -> 500ms -> 1s -> 2s -> 4s -> 8s (capped at maxDelay)
    const exponentialDelay = Math.min(
      this.maxDelay, // 2000ms max from constructor
      250 * Math.pow(2, retryCount)
    );
    
    // Add jitter (±25%) to avoid thundering herd and improve recovery patterns
    const jitter = exponentialDelay * 0.25 * (Math.random() - 0.5);
    
    const finalDelay = Math.floor(exponentialDelay + jitter);
    
    return finalDelay;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Utility function to detect circuit breaker errors
  isCircuitBreakerError(error) {
    if (!error || !error.message) return false;
    
    const message = error.message.toLowerCase();
    return message.includes('circuit breaker') && 
           (message.includes('open') || message.includes('opened'));
  }

  // All resource-specific methods removed - global rate limiting handles everything

  // Reset monitoring stats for new export operation (called at start of export)
  resetMonitoringStats() {
    console.log('🔄 Resetting rate limiting monitoring stats for new export operation');

    // Reset counters for all services
    Object.values(this.serviceConfig).forEach(config => {
      config.requestCount = 0;
    });
    this.rateLimitViolationCount = 0;

    console.log('✅ Rate limiting monitoring stats reset complete');
  }

  // Get comprehensive monitoring stats for all services
  getMonitoringStats() {
    const stats = {
      services: {},
      circuitBreakers: {},
      resourceSpecific: {},
      summary: {
        totalRequests: 0,
        totalViolations: this.rateLimitViolationCount,
        activeCircuitBreakers: 0,
        rateLimitingEfficiency: 0
      }
    };

    // Service-level stats
    Object.entries(this.serviceConfig).forEach(([service, config]) => {
      stats.services[service] = {
        requestCount: config.requestCount,
        availableTokens: Math.floor(config.tokens),
        refillRatePerSec: config.refillRatePerSec,
        avgDelay: config.delay,
        concurrency: this.serviceLimiters[service] ? this.serviceLimiters[service].activeCount : 0
      };
      stats.summary.totalRequests += config.requestCount;
    });

    // Circuit breaker stats
    this.circuitBreakersByType.forEach((isOpen, resourceType) => {
      stats.circuitBreakers[resourceType] = {
        isOpen: isOpen,
        consecutiveErrors: this.getConsecutiveErrors(resourceType),
        resetTime: this.getCircuitBreakerResetTime(resourceType)
      };
      if (isOpen) stats.summary.activeCircuitBreakers++;
    });

    // Resource-specific configuration stats
    Object.entries(this.resourceSpecificDelays).forEach(([resource, delay]) => {
      stats.resourceSpecific[resource] = {
        configuredDelay: delay,
        hasSpecificLimiter: !!this.resourceSpecificLimiters[resource],
        limiterConcurrency: this.resourceSpecificLimiters[resource] ? 1 : 'inherited'
      };
    });

    // Calculate efficiency metric
    if (stats.summary.totalRequests > 0) {
      stats.summary.rateLimitingEfficiency =
        Math.max(0, 100 - (this.rateLimitViolationCount / stats.summary.totalRequests * 100));
    }

    return stats;
  }

  // Adjust concurrency dynamically based on error rate
  // Log detailed rate limiting performance report
  logPerformanceReport() {
    const stats = this.getMonitoringStats();
    console.log('\n📊 RATE LIMITING PERFORMANCE REPORT');
    console.log('=====================================');

    // Summary
    console.log(`📊 Summary:`);
    console.log(`   Total Requests: ${stats.summary.totalRequests}`);
    console.log(`   Rate Limit Violations: ${stats.summary.totalViolations}`);
    console.log(`   Active Circuit Breakers: ${stats.summary.activeCircuitBreakers}`);
    console.log(`   Efficiency: ${stats.summary.rateLimitingEfficiency.toFixed(1)}%`);

    // Service breakdown
    console.log(`\n📊 Service Breakdown:`);
    Object.entries(stats.services).forEach(([service, data]) => {
      console.log(`   ${service}: ${data.requestCount} requests, ${data.avgDelay}ms delay, ${data.concurrency} active`);
    });

    // Resource-specific configuration
    console.log(`\n🔒 Resource-Specific Limits:`);
    Object.entries(stats.resourceSpecific).forEach(([resource, data]) => {
      console.log(`   ${resource}: ${data.configuredDelay}ms delay, concurrency: ${data.limiterConcurrency}`);
    });

    // Circuit breaker status
    if (Object.keys(stats.circuitBreakers).length > 0) {
      console.log(`\n🚫 Circuit Breaker Status:`);
      Object.entries(stats.circuitBreakers).forEach(([resource, data]) => {
        const status = data.isOpen ? `OPEN (resets in ${Math.round((data.resetTime - Date.now())/1000)}s)` : 'CLOSED';
        console.log(`   ${resource}: ${status}, errors: ${data.consecutiveErrors}`);
      });
    }

    console.log('=====================================\n');
  }

  adjustConcurrency(increase = false) {
    if (increase && this.concurrency < 3) {
      this.concurrency++;
      this.limiter = pLimit(this.concurrency);
    } else if (!increase && this.concurrency > 1) {
      this.concurrency--;
      this.limiter = pLimit(this.concurrency);
    }
  }
}

// Create and export singleton instance with service-specific configuration
export const requestQueueManager = new RequestQueueManager({
  maxDelay: 2000         // Max backoff delay for all services
});

export default RequestQueueManager;